import {Component, inject, OnInit, signal, TemplateRef, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MenusServices} from '@app/services/menus.service';
import {BaseComponent} from '@app/components/base/base.component';
import {environment} from '@env/environment.local';
import {queryParamsQuote} from '../model/standard-quote.model';
import {
  CustomerFilterType,
  customerTypes,
  isStandardQuoteCustomerOnlyLayout,
  STANDARD_QUOTE_LIST_STATUS_FILTER_OPTIONS,
  standardQuoteStatusDisplayLabel,
  standardQuoteStatusListCssClass,
} from '../../open-quote/model/open-quote.model';
import {AuthService} from '@app/common/auth.service';
import {NotificationService} from '@app/services/notifications.service';
import {StandardQuoteService} from '../standard-quote-service.service';
import {ModalService} from '@app/components/modal/modal.service';
import {MessagesUtil} from "@app/core/model/Message";
import { CLIENT_SERVICE } from '@app/pages/clients/interfaces/endpoints';
import { ResponsiveService } from '@app/services/responsive.service';
import { finalize } from 'rxjs';
import { RolesDealer, UserType } from '@app/models/roles.enum';

@Component({
  template: ""
})
export class ListQuoteStandardWrapperComponent
  extends BaseComponent
  implements OnInit {
  @ViewChild('templateConfirmation') template!: TemplateRef<any>;
  @ViewChild('templateDuplicateConfirmation') templateDuplicate!: TemplateRef<any>;
  isLoading: boolean = false;
  hasData: boolean = false;
  statusQuote: string | null = null;

  data: any[] = [];

  total: number = 0;

  pageSize: number = environment.pageSize;
  pageIndex: number = 1;

  params: queryParamsQuote = {
    filter: {
      dealerId: null,
      userType: null,
      page: this.pageIndex,
      pageSize: this.pageSize,
      me: false,
      customerName: null,
      customerId: null,
      quoteName: null,
      status: null,
      from: null,
      to: null,
      isExternalCustomer: null,
      validStatus: null,
      dealerBranchId: null,
    },
  };

  status: any = {
    data: [...STANDARD_QUOTE_LIST_STATUS_FILTER_OPTIONS],
    placeholder: 'Buscar por estado',
    filterType: 'status',
  };

  validStatusFilter: any = {
    data: [
      { label: 'Todas', value: null },
      { label: 'Vigentes', value: true },
      { label: 'Vencidas', value: false },
    ],
    placeholder: 'Vigencia',
    filterType: 'validStatus',
  };

  searchNameQuote: any = {placeholder: 'Buscar cotizacion', value: ''};
  searchNameCustomer: any = {placeholder: 'Buscar cliente', value: ''};

  userId: string;
  dealerId: number;
  user: string
  reset: boolean = false;
  idQuote: number;

  /** Cotización en la que se está llamando a extender vigencia (evita doble clic). */
  extendingQuoteId: number | null = null;

  title: string = 'Eliminar cotización';
  subtitle: string = '';
  btn1: string = 'Cancelar';
  btn2: string = 'Eliminar';

  readonly readonlyAdditionalSearchClientParams = {
    rfc: null,
    personType: null,
    page: 1,
    pageSize: 30,
  }

  customerTypeList: any[] = customerTypes;
  isActiveClientCatalog = environment.active_client_catalog;

  readonly activeQuoteListValidStatusFilter = environment.activeQuoteListValidStatusFilter;
  readonly showQuoteValidityListFilter = environment.showQuoteValidityListFilter;

  /**
   * Filtro "Mis cotizaciones" (ver solo propias vs. cotizaciones de terceros en el concesionario).
   * Solo aplica a Enlace financiero y usuarios internos (Dashboard).
   */
  get showThirdPartyQuotesFilter(): boolean {
    const userTypes = this.authService.getCurrentUserType;
    if (userTypes === UserType.internal) {
      return true;
    }
    if (Array.isArray(userTypes) && userTypes.includes(UserType.internal)) {
      return true;
    }
    const roles = this.authService.currentRoles;
    return Array.isArray(roles) && roles.includes(RolesDealer.financial);
  }

  readonly responsiveService = inject(ResponsiveService);
  readonly quoteService = inject(StandardQuoteService);
  readonly authService = inject(AuthService);
  readonly routerNavigate = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly _notification = inject(NotificationService);

  openRowOption = signal<string>('');

  constructor(
    route: ActivatedRoute,
    menu: MenusServices,
    notification: NotificationService
  ) {
    super(route, menu, notification);
  }

  ngOnInit() {
    this.trackerService.track("UserLoadQuoteList", {
      dealerId: this.authService.getCurrentDealerId,
    });
    this.sendDataToMenu();
    this.userId = this.authService.currentUserIds;
    this.user = this.authService.getCurrentUserType;
    this.dealerId = this.authService.getCurrentDealerId
    this.params.filter.me = true;

    const roles = this.authService.currentRoles;
    const isFinancial = Array.isArray(roles) && roles.includes(RolesDealer.financial);
    if (isFinancial) {
      this.params.filter.dealerBranchId = this.authService.getCurrentDealerBranchId;
    }

    this.params.filter.validStatus =
      environment.quoteListValidStatusDefault ?? null;
    this.getQuotes();
    this.quoteService.formData = null;
    this.responsiveService.onListen();
  }

  protected buildStandardQuoteListPayload(): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      ...this.params.filter,
      dealerId: this.dealerId,
      userType: this.user[0],
    };

    const roles = this.authService.currentRoles;
    const isFinancial = Array.isArray(roles) && roles.includes(RolesDealer.financial);

    if (isFinancial) {
      payload['dealerBranchId'] = this.authService.getCurrentDealerBranchId;
    }

    if (!this.showThirdPartyQuotesFilter) {
      payload['me'] = true;
    }
    if (!environment.activeQuoteListValidStatusFilter) {
      delete payload['validStatus'];
    } else if (!environment.showQuoteValidityListFilter) {
      payload['validStatus'] = environment.quoteListValidStatusDefault ?? null;
    }
    return payload;
  }

  getQuotes() {
    this.isLoading = true;
    this.quoteService.getQuotesOptimized(this.buildStandardQuoteListPayload()).subscribe({
      next: (response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        const userBranchId = this.authService.getCurrentDealerBranchId;
        const roles = this.authService.currentRoles;
        const isFinancial = Array.isArray(roles) && roles.includes(RolesDealer.financial);

        this.data = rows.filter((item: { dealerId: number; creationUser: string; wholeSaleId?: number }) => {
          // Si el backend ya filtra por dealerId y wholeSaleId, este filtro es solo un respaldo de seguridad
          const baseFilter = item.dealerId === this.dealerId || item.creationUser === this.userId;
          if (isFinancial && userBranchId) {
            return baseFilter && item.wholeSaleId === userBranchId;
          }
          return baseFilter;
        });

        this.total = response?.totalElements ?? this.data.length;
        this.pageIndex = Math.max(0, (response?.page ?? 1) - 1);
        this.hasData = this.data.length > 0;
        this.isLoading = false;
        this.data.forEach(quote => {
          quote.invalidFor = quote.model.enabled === false && quote.status == 'DRAFT' ? 'Unidad ya no disponible' : null;
        })

      },
      error: (error) => {
        this.isLoading = false;
      },
    });
  }

  handlePage(e: any) {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.params.filter.page = e.pageIndex + 1;
    this.params.filter.pageSize = e.pageSize;
    this.getQuotes();
  }

  deleteQuoteById(): void {
    this.closeModal();
    this.isLoading = true;
    this.quoteService.deleteQuote(this.idQuote).subscribe({
      next: (response) => {
        this.trackerService.track("UserDeleteQuote", {
          quoteId: this.idQuote,
        });
        this.showNotificationSuccess(
          'La cotización ha sido eliminada con éxito.'
        );
        this.isLoading = false;
        this.getQuotes();
      },
      error: (error) => {
        this.showNotificationError(
          'Ha ocurrido un error, la cotización no se pudo eliminar.'
        );
        this.isLoading = false;
      },
    });
  }


  duplicateQuoteById(): void {
    this.closeModal();
    this.isLoading = true;
    this.quoteService.duplicateStandardQuoteById(this.idQuote).subscribe({
      next: (response) => {
        this.trackerService.track("UserDuplicateQuote", {
          quoteId: this.idQuote,
        });
        if (response.status) {
          this.showNotificationSuccess(
            'La cotización ha sido duplicada con éxito.'
          );
          this.isLoading = false;
          this.routerNavigate.navigate(['/standard-quote/edit-quote/' + response.data.id])
        } else if (response.errors && response.errors.length > 0) {
          const key = response.errors[0];
          const message = MessagesUtil[key] as string || 'No se puede duplicar la cotización.';
          this.showNotificationError(message);
          this.isLoading = false;
          this.getQuotes();
        } else{
          this.showNotificationError('No se puede duplicar la cotización.');
          this.isLoading = false;
          this.getQuotes();
        }
      },
      error: (error) => {
        this.trackerService.track("UserDuplicateQuoteError", {
          quoteId: this.idQuote,
          message: error?.message ?? ""
        });
        this.showNotificationError(
          'Ha ocurrido un error, la cotización no se pudo duplicar.'
        );
        this.isLoading = false;
      },
    });
  }

  closeModal() {
    this.modalService.closeModal();
  }

  canExtendStandardQuoteValidity(quote: {
    valid?: boolean;
    minQuoteValidityDays?: number;
    creationUser?: string | number;
    validityExtensionApplied?: boolean;
    hasInactiveFinancialSetup?: boolean;
  }): boolean {
    return (
      this.showQuoteValidityListFilter &&
      quote?.valid === false &&
      quote?.minQuoteValidityDays != null &&
      quote?.validityExtensionApplied !== true &&
      quote?.hasInactiveFinancialSetup !== true &&
      String(this.userId) === String(quote.creationUser)
    );
  }

  extendQuoteValidity(quote: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.extendingQuoteId != null) {
      return;
    }
    this.extendingQuoteId = quote.id;
    this.quoteService.extendStandardQuoteValidity(quote.id).pipe(
      finalize(() => {
        this.extendingQuoteId = null;
      })
    ).subscribe({
      next: (response) => {
        if (response?.data) {
          quote.validityEndDate = response.data.validityEndDate;
          quote.valid = response.data.valid;
          quote.minQuoteValidityDays = response.data.minQuoteValidityDays;
          quote.validityExtensionApplied = response.data.validityExtensionApplied ?? true;
          quote.hasInactiveFinancialSetup = response.data.hasInactiveFinancialSetup;
        }
        this.showNotificationSuccess('Vigencia extendida correctamente.');
      },
      error: (error) => {
        const msg =
          error?.error?.errors?.[0] ??
          'No se pudo extender la vigencia de la cotización.';
        this.showNotificationError(typeof msg === 'string' ? msg : String(msg));
      },
    });
  }

  quoteIsTerminalCustomerOnly(quote: { status?: string }): boolean {
    return isStandardQuoteCustomerOnlyLayout(quote?.status);
  }

  getStatus(status: string | null | undefined): string {
    return standardQuoteStatusDisplayLabel(status);
  }

  getStatusCssClass(status: string | null | undefined): string {
    return standardQuoteStatusListCssClass(status);
  }

  filter(value: any, typeSelect: string) {
    switch (typeSelect) {
      case 'status':
        this.params.filter.status = value;
        break;
      case 'quoteName':
        this.params.filter.quoteName = value == '' ? null : value;
        break;
      case 'customerName':
        this.params.filter.customerName = value == '' ? null : value;
        break;
      case 'me':
        if (!this.showThirdPartyQuotesFilter) {
          this.params.filter.me = true;
        } else {
          this.params.filter.me = value;
        }
        this.search();
        break;
      case 'from':
        this.params.filter.from = value;
        break;
      case 'to':
        this.params.filter.to = value;
        break;
      case 'isExternalCustomer':
        if (value === CustomerFilterType.WITH_RFC) {
          this.params.filter.isExternalCustomer = true;
        } else if (value === CustomerFilterType.WITHOUT_RFC) {
          this.params.filter.isExternalCustomer = false;
        } else {
          this.params.filter.isExternalCustomer = null;
        }
        break;
      case 'validStatus':
        this.params.filter.validStatus = value;
        break;
    }
  }

  search() {
    this.trackerService.track("UserFilterQuotes", {
        params: this.params.filter
    });
    this.params.filter.page = 1;
    this.getQuotes();
  }

  resetFilters() {
    this.params.filter = {
      page: 1,
      pageSize: this.pageSize,
      me: this.params.filter.me,
      customerName: null,
      customerId: null,
      quoteName: null,
      status: null,
      from: null,
      to: null,
      dealerId: null,
      userType: null,
      isExternalCustomer: null,
      dealerBranchId: this.params.filter.dealerBranchId,
      validStatus: environment.showQuoteValidityListFilter
        ? null
        : (environment.quoteListValidStatusDefault ?? null),
    };
    this.reset = !this.reset;
    this.getQuotes();
  }

  openModalConfirmation(quote: any) {
    this.trackerService.track("UserStartDeleteQuote", {
      quoteId: quote.id,
      quoteName: quote.quoteName,
      customerName: quote.customerName,
      customerId: quote.customerId,
      dealerBranchId: quote.wholeSaleId,
      dealerId: quote.dealerId,
    });
    this.btn2 = 'Eliminar';
    this.title = 'Eliminar cotización';
    this.idQuote = quote.id;
    this.subtitle = `¿Confirma la eliminación de la cotización "${quote.quoteName}"?`;

    this.modalService.component = this.template;
    this.modalService.openModal();
  }

  openModalToDuplicateStandardQuote(quote: any) {
    this.trackerService.track("UserStartDuplicateQuote", {
      quoteId: quote.id,
      quoteName: quote.quoteName,
      customerName: quote.customerName,
      customerId: quote.customerId,
      dealerBranchId: quote.wholeSaleId,
      dealerId: quote.dealerId,
    });
    this.btn2 = 'Duplicar';
    this.title = 'Duplicar cotización';
    this.idQuote = quote.id;
    this.subtitle = `¿Confirma el duplicado de la cotización "${quote.quoteName}"?`;
    this.modalService.component = this.templateDuplicate;
    this.modalService.openModal();
  }


  notificationTest() {
    this._notification.clearMessages();
    this._notification.showMessage(
      ['Esta es una prueba de notificación'],
      'error'
    );
  }

  notificationTestSuccess() {
    this._notification.clearMessages();
    this._notification.showMessage(
      ['Esta es una prueba de notificación'],
      'success'
    );
  }

  showCustomerName(customer: any) {
    if (!customer) return '';
    if(!customer.firstName) return customer.customerName;
    return ((customer.firstName ?? '') + ' ' + (customer.secondName ?? '') + ' ' + (customer.lastName ?? '') + ' ' + (customer.mothersLastName ?? ''));
  }


  getAllClientsUrl(): string {
    return `${environment.API_CLIENTS_SERVICE}${CLIENT_SERVICE.CLIENTS_SEARCH_ALL}`;
  }

  handleSearchClient(client: any) {
    const isFromProlisnet = this.params.filter.isExternalCustomer;
    if(!client && isFromProlisnet === true) return;
    this.params.filter.customerId = client?.id || null;
    this.reset = false;
  }

  handleTextChange(text: string) {
    // const isFromProlisnet = this.params.filter.isExternalCustomer;
    // if(isFromProlisnet === CustomerFilterType.WITH_RFC) return;
    this.params.filter.customerName = !text ? null : text;
    this.reset = false;
  }

  getRowKey(id: string, i: number) {
    return `${id}-${i}`;
  }

  getMobileMenuTriggerId(id: string) {
    return `menu-${id}`;
  }

  isMenuRowOpen(id: string) {
    return this.openRowOption() === id;
  }

  toggleMobileMenuOptions(e: MouseEvent, updated: string) {
    e.preventDefault();
    this.openRowOption.update((value) => (value !== '' ? '' : updated));
  }

  closeMobileMenuOptions() {
    this.openRowOption.set('');
  }
}
